import { AnimatePresence } from 'motion/react'
import * as motion from 'motion/react-client'
import { Dispatch, FC, ReactNode, SetStateAction, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { observer } from 'mobx-react-lite'
import {
	EquipmentFilterValue,
	ExerciseFilterStore,
	ExerciseTypeFilterValue,
	useExerciseFilterStore,
} from '@entities/exercise'
import {
	FILTER_GROUP_CHIPS,
	FILTER_MUSCLE_SECTIONS,
} from '@pages/TrainingConstructor/ui/filter/filterCatalog'
import { FilterMuscleCard } from '@pages/TrainingConstructor/ui/filter/muscleCard'

interface FiltersProps {
	isFiltersOpen: boolean
	setIsFiltersOpen: Dispatch<SetStateAction<boolean>>
}

type FilterToggleOption<T extends string> = {
	label: string
	value: T
}

const TYPE_OPTIONS: FilterToggleOption<ExerciseTypeFilterValue>[] = [
	{ label: 'Силовое', value: 'strength' },
	{ label: 'Кардио', value: 'cardio' },
]

const EQUIPMENT_OPTIONS: FilterToggleOption<EquipmentFilterValue>[] = [
	{ label: 'С оборудованием', value: 'requires_equipment' },
	{ label: 'Без оборудования', value: 'no_equipment' },
]

const FilterSection: FC<{ title: string; children: ReactNode }> = ({
	title,
	children,
}) => (
	<section className="training-filter-sheet-section">
		<h3>{title}</h3>
		{children}
	</section>
)

export const Filters: FC<FiltersProps> = observer(
	({ isFiltersOpen, setIsFiltersOpen }) => {
		const exerciseFilterStore: ExerciseFilterStore = useExerciseFilterStore()

		useEffect(() => {
			if (isFiltersOpen) {
				exerciseFilterStore.rollbackDraftToAccepted()
			}
		}, [exerciseFilterStore, isFiltersOpen])

		const closeWithoutApply = () => {
			exerciseFilterStore.rollbackDraftToAccepted()
			setIsFiltersOpen(false)
		}

		const applyFilters = () => {
			exerciseFilterStore.confirmFilters('advanced')
			setIsFiltersOpen(false)
		}

		return createPortal(
			<>
				<AnimatePresence initial={false}>
					{isFiltersOpen && (
						<motion.div
							className="training-filter-sheet-overlay"
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
							onClick={closeWithoutApply}
						/>
					)}
				</AnimatePresence>

				<AnimatePresence initial={false}>
					{isFiltersOpen && (
						<motion.div
							className="training-filter-sheet"
							role="dialog"
							aria-modal="true"
							aria-labelledby="training-filter-title"
							initial={{ y: '100%' }}
							animate={{ y: 0 }}
							exit={{ y: '100%' }}
							transition={{ type: 'spring', damping: 28, stiffness: 220 }}
							key="training-filter-sheet"
						>
							<div className="training-filter-sheet-handle" aria-hidden="true" />
							<header className="training-filter-sheet-header">
								<h2 id="training-filter-title">Фильтр</h2>
								<button
									type="button"
									className="training-filter-sheet-close"
									aria-label="Закрыть фильтр"
									onClick={closeWithoutApply}
								>
									<X aria-hidden="true" />
								</button>
							</header>

							<div className="training-filter-sheet-content">
								<FilterSection title="Тип упражнения">
									<div className="training-filter-toggle-grid">
										{TYPE_OPTIONS.map((option) => {
											const isSelected =
												exerciseFilterStore.selectedExerciseTypes.includes(
													option.value,
												)

											return (
												<button
													key={option.value}
													type="button"
													className={`training-filter-pill ${isSelected ? 'is-selected' : ''}`}
													aria-pressed={isSelected}
													onClick={() =>
														exerciseFilterStore.toggleExerciseType(option.value)
													}
												>
													{option.label}
												</button>
											)
										})}
									</div>
								</FilterSection>

								<FilterSection title="Оборудование">
									<div className="training-filter-toggle-grid">
										{EQUIPMENT_OPTIONS.map((option) => {
											const isSelected =
												exerciseFilterStore.selectedEquipmentTags.includes(
													option.value,
												)

											return (
												<button
													key={option.value}
													type="button"
													className={`training-filter-pill ${isSelected ? 'is-selected' : ''}`}
													aria-pressed={isSelected}
													onClick={() =>
														exerciseFilterStore.toggleEquipmentTag(option.value)
													}
												>
													{option.label}
												</button>
											)
										})}
									</div>
								</FilterSection>

								<FilterSection title="Группы мышц">
									<div className="training-filter-group-chips">
										{FILTER_GROUP_CHIPS.map((chip) => {
											const isSelected = chip.codes.every((code) =>
												exerciseFilterStore.includedMuscles.includes(code),
											)

											return (
												<button
													key={chip.label}
													type="button"
													className={`training-filter-group-chip ${isSelected ? 'is-selected' : ''}`}
													aria-pressed={isSelected}
													onClick={() =>
														exerciseFilterStore.toggleMuscleGroup(chip.codes)
													}
												>
													{chip.label}
												</button>
											)
										})}
									</div>
								</FilterSection>

								{FILTER_MUSCLE_SECTIONS.map((section) => (
									<section
										key={section.title}
										className="training-filter-muscle-section"
									>
										<h3>{section.title}</h3>
										<div className="training-filter-muscle-grid">
											{section.items.map((muscle) => (
												<FilterMuscleCard
													key={muscle.code}
													muscle={muscle}
													isSelected={exerciseFilterStore.includedMuscles.includes(
														muscle.code,
													)}
													onToggle={() =>
														exerciseFilterStore.toggleIncludedMuscleCodes(
															muscle.code,
														)
													}
												/>
											))}
										</div>
									</section>
								))}
							</div>

							<footer className="training-filter-sheet-footer">
								<button
									type="button"
									className="training-filter-reset-button"
									aria-label="Закрыть фильтр снизу"
									onClick={closeWithoutApply}
								>
									<X aria-hidden="true" />
								</button>
								<button
									type="button"
									className="training-filter-apply-button"
									onClick={applyFilters}
								>
									Применить фильтр
								</button>
							</footer>
						</motion.div>
					)}
				</AnimatePresence>
			</>,
			document.body,
		)
	},
)
